import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { UserPlus, Trash2, Shield, User as UserIcon, Loader2, X, Check } from "lucide-react";

const MODULES = [
  { key: "aytemiz", label: "Aytemiz & Yakıt" },
  { key: "cards", label: "Kredi Kartları" },
  { key: "loans", label: "Krediler" },
  { key: "calendar", label: "Ödeme Takvimi" },
  { key: "ai", label: "Yapay Zeka Asistanı" },
  { key: "mail", label: "E-postalar" },
];
const ACTIONS = [
  { key: "view", label: "Görüntüle" },
  { key: "add", label: "Ekle / Düzenle" },
  { key: "pay_delete", label: "Öde / Sil" },
];

const emptyForm = () => ({ email: "", name: "", password: "", permissions: {} });

export default function Users() {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // user id or null (new)
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/users");
      setUsers(res.data || []);
      setDenied(false);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditing(u.id);
    setForm({ email: u.email, name: u.name || "", password: "", permissions: JSON.parse(JSON.stringify(u.permissions || {})) });
    setShowForm(true);
  };

  const toggle = (mod, act) => {
    setForm((f) => {
      const perms = { ...(f.permissions || {}) };
      const list = new Set(perms[mod] || []);
      if (list.has(act)) list.delete(act);
      else {
        list.add(act);
        if (act !== "view") list.add("view"); // add/pay imply view
      }
      perms[mod] = Array.from(list);
      if (perms[mod].length === 0) delete perms[mod];
      return { ...f, permissions: perms };
    });
  };

  const has = (mod, act) => (form.permissions?.[mod] || []).includes(act);

  const save = async () => {
    if (!editing && (!form.email || !form.password)) {
      toast.error("E-posta ve şifre zorunludur");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/users/${editing}`, {
          name: form.name,
          password: form.password || undefined,
          permissions: form.permissions,
        });
        toast.success("Kullanıcı güncellendi");
      } else {
        await api.post("/users", {
          email: form.email,
          name: form.name,
          password: form.password,
          permissions: form.permissions,
        });
        toast.success("Kullanıcı oluşturuldu");
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İşlem başarısız");
    }
    setSaving(false);
  };

  const remove = async (u) => {
    if (!window.confirm(`${u.email} silinsin mi?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Kullanıcı silindi");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Silinemedi");
    }
  };

  if (denied || !isAdmin) {
    return (
      <div className="p-8 max-w-3xl">
        <PageHeader title="Kullanıcılar" testid="users-header" />
        <div className="card-surface p-10 text-center text-[#94A3B8]">Bu sayfayı görüntüleme yetkiniz yok.</div>
      </div>
    );
  }

  return (
    <div className="p-5 md:p-8 max-w-4xl" data-testid="users-page">
      <PageHeader
        title="Kullanıcılar & Yetkiler"
        subtitle="Ekibinize kullanıcı ekleyin ve her birinin hangi bölümleri görüp hangi işlemleri yapabileceğini belirleyin."
        testid="users-header"
        action={
          <button
            onClick={openNew}
            data-testid="add-user-button"
            className="flex items-center gap-2 bg-[#E65100] hover:bg-[#FF6D00] text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
          >
            <UserPlus className="w-4 h-4" /> Yeni Kullanıcı
          </button>
        }
      />

      <div className="space-y-3">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#E65100]" /></div>
        ) : (
          users.map((u) => (
            <div key={u.id} className="card-surface p-4 flex items-center gap-4 fade-up" data-testid="user-item">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${u.is_owner ? "bg-[#E65100]/20" : "bg-[#1B2430]"}`}>
                {u.is_owner ? <Shield className="w-5 h-5 text-[#E65100]" /> : <UserIcon className="w-5 h-5 text-[#94A3B8]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{u.name || u.email}</div>
                <div className="text-[12px] text-[#94A3B8] truncate">{u.email}</div>
                {!u.is_owner && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {MODULES.filter((m) => (u.permissions?.[m.key] || []).length).map((m) => (
                      <span key={m.key} className="text-[10px] px-2 py-0.5 rounded-full bg-[#1B2430] text-[#CBD5E1]">
                        {m.label}
                      </span>
                    ))}
                    {MODULES.every((m) => !(u.permissions?.[m.key] || []).length) && (
                      <span className="text-[10px] text-[#64748B]">Yetki tanımlı değil</span>
                    )}
                  </div>
                )}
              </div>
              {u.is_owner ? (
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#E65100]/15 text-[#E65100] font-medium">Yönetici</span>
              ) : (
                <div className="flex items-center gap-1.5">
                  <button onClick={() => openEdit(u)} data-testid="edit-user-button" className="text-xs px-3 py-1.5 rounded-lg bg-[#1B2430] hover:bg-[#232E3F] text-[#CBD5E1]">
                    Yetkiler
                  </button>
                  <button onClick={() => remove(u)} data-testid="delete-user-button" className="w-8 h-8 rounded-lg bg-[#1B2430] hover:bg-[#EF4444]/20 flex items-center justify-center text-[#94A3B8] hover:text-[#EF4444]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 p-0 md:p-4" onClick={() => setShowForm(false)}>
          <div className="bg-[#0F141C] border border-[#253043] rounded-t-2xl md:rounded-2xl w-full md:max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-form">
            <div className="flex items-center justify-between p-5 border-b border-[#253043] sticky top-0 bg-[#0F141C]">
              <h3 className="font-display font-bold text-lg">{editing ? "Yetkileri Düzenle" : "Yeni Kullanıcı"}</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-lg hover:bg-[#1B2430] flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              {!editing && (
                <>
                  <Field label="E-posta">
                    <input data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="kullanici@ornek.com" />
                  </Field>
                  <Field label="Şifre">
                    <input data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder="Geçici şifre" />
                  </Field>
                </>
              )}
              <Field label="Ad (opsiyonel)">
                <input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Örn. Ahmet" />
              </Field>
              {editing && (
                <Field label="Yeni Şifre (boş bırakılırsa değişmez)">
                  <input data-testid="user-newpassword-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder="••••••" />
                </Field>
              )}

              <div>
                <div className="text-xs text-[#94A3B8] mb-2">Yetkiler</div>
                <div className="space-y-2">
                  {MODULES.map((m) => (
                    <div key={m.key} className="bg-[#111620] rounded-xl p-3">
                      <div className="text-sm font-medium mb-2">{m.label}</div>
                      <div className="flex flex-wrap gap-2">
                        {ACTIONS.map((a) => {
                          const active = has(m.key, a.key);
                          return (
                            <button
                              key={a.key}
                              onClick={() => toggle(m.key, a.key)}
                              data-testid={`perm-${m.key}-${a.key}`}
                              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${active ? "bg-[#E65100] border-[#E65100] text-white" : "bg-transparent border-[#253043] text-[#94A3B8] hover:border-[#475569]"}`}
                            >
                              {active && <Check className="w-3 h-3" />}
                              {a.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-[#253043] sticky bottom-0 bg-[#0F141C]">
              <button onClick={save} disabled={saving} data-testid="user-save-button" className="w-full flex items-center justify-center gap-2 bg-[#E65100] hover:bg-[#FF6D00] disabled:opacity-60 text-white py-3 rounded-xl font-semibold transition-colors">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? "Kaydet" : "Kullanıcı Oluştur"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full px-3.5 py-2.5 rounded-xl bg-[#111620] border border-[#253043] focus:border-[#E65100] outline-none transition-colors text-sm";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-[#94A3B8] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
